import { useState, useCallback } from 'react';

type AccordionGroupControlProps = {
  isOpen: boolean;
  onToggle: (isOpen: boolean) => void;
};

type UseAccordionGroupOptions = {
  /** Key of the accordion that should be open initially */
  defaultOpen?: string | null;
};

/**
 * Hook for managing exclusive accordion open/close state.
 * Only one accordion can be open at a time — opening one closes the others.
 *
 * @param options - Configuration options
 * @param options.defaultOpen - Key of the initially open accordion (default: null)
 * @returns Object with `getAccordionProps` to spread onto each Accordion
 *
 * @example
 * ```tsx
 * const { getAccordionProps } = useAccordionGroup({ defaultOpen: 'section-1' });
 *
 * <Accordion {...getAccordionProps('section-1')} title="Section 1">...</Accordion>
 * <Accordion {...getAccordionProps('section-2')} title="Section 2">...</Accordion>
 * ```
 */
const useAccordionGroup = ({ defaultOpen = null }: UseAccordionGroupOptions = {}) => {
  const [openKey, setOpenKey] = useState<string | null>(defaultOpen);

  const getAccordionProps = useCallback(
    (key: string): AccordionGroupControlProps => ({
      isOpen: openKey === key,
      onToggle: (isOpen: boolean) => {
        setOpenKey(isOpen ? key : null);
      },
    }),
    [openKey]
  );

  return { getAccordionProps, openKey, setOpenKey };
};

export default useAccordionGroup;
